/**
 * VersionHistoryButton — the one trigger for the CRM version history, dropped
 * into any card header that shows "Published v3" (study overview / script and
 * other text artifacts, prescreen, forms, review criteria, prompts, flows).
 *
 * CRM admins only (the people who set studies up): for anyone else it renders
 * nothing. Owns the modal's open state. Its click never reaches a clickable
 * row or card it sits inside.
 */
import { useCallback, useState } from 'react';
import type { MouseEvent } from 'react';
import { History } from 'lucide-react';
import { useIsCRMAdmin } from '../../lib';
import { iconBtnCls } from '../../../ui/styles';
import VersionHistoryModal from './VersionHistoryModal';
import type { VersionHistoryKind, VersionHistoryParams } from './types';

export interface VersionHistoryButtonProps {
  kind: VersionHistoryKind;
  params: VersionHistoryParams;
  /** Fallback modal title until the server's title arrives. */
  title?: string;
  /** Version to preselect when the modal opens. */
  initialVersionId?: string;
  /** Visible label (default 'History'); the accessible name for 'icon'. */
  label?: string;
  /** 'button' (default) = compact outline button · 'link' = inline text link · 'icon' = icon-only. */
  variant?: 'button' | 'link' | 'icon';
  className?: string;
}

/** The spec's secondary/outline button, compacted to sit beside a "Published v3" pill. */
const BUTTON_CLS =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0 sm:py-1';

const LINK_CLS =
  'inline-flex min-h-[44px] items-center gap-1 rounded text-xs font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0';

export default function VersionHistoryButton({
  kind,
  params,
  title,
  initialVersionId,
  label = 'History',
  variant = 'button',
  className = '',
}: VersionHistoryButtonProps) {
  const isAdmin = useIsCRMAdmin();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  if (!isAdmin) return null;

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const accessibleName = label === 'History' ? 'Version history' : label;

  let trigger: JSX.Element;
  if (variant === 'icon') {
    trigger = (
      <button
        type="button"
        onClick={onClick}
        aria-label={accessibleName}
        aria-haspopup="dialog"
        title="Version history"
        className={`${iconBtnCls} ${className}`}
      >
        <History className="h-4 w-4" aria-hidden="true" />
      </button>
    );
  } else {
    trigger = (
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        title="Version history — previous versions, what changed, and who changed it"
        className={`${variant === 'link' ? LINK_CLS : BUTTON_CLS} ${className}`}
      >
        <History className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </button>
    );
  }

  return (
    <>
      {trigger}
      <VersionHistoryModal
        isOpen={open}
        onClose={close}
        kind={kind}
        params={params}
        title={title}
        initialVersionId={initialVersionId}
      />
    </>
  );
}
